import * as machinelogic from '@vse/main';
import { pythonControl } from './pythonControl';
import {
  APPLICATION_CARDS_QUERY_SELECTOR,
  PLAYING_APPLICATION_NAME_QUERY_SELECTOR,
  PLAYING_APPLICATION_STATUS_QUERY_SELECTOR,
  SEQUENCE_LIST_QUERY_SELECTOR,
} from './components';

import { FeatureFlags } from '@vse/featureFlags';

// TODO GD Use the enums from machinelogic
// State of the state machine
const gStateEnum = {
  inactive: 'No Active Application',
  paused: 'paused',
  running: 'running',
  completed: 'completed',
  invalid: 'invalid',
  not_started: 'not started',
  stopped: 'stopped', // Note : the "stopped" state is created here, and never received from the exec-engine.
};

const gSeqStateEnum = {
  inactive: 'Not started',
  paused: 'Paused',
  running: 'Running',
  completed: 'Completed',
  stopped: 'Stopped',
  waiting: 'Waiting for ',
};

/**
 * Deactivates the autoLaunch flag of the provided app.
 *
 * @param {LaunchableApp} launchableApp
 * @return {JQuery.jqXHR|void}
 */
function turnOffApplicationAutoLaunch (launchableApp) {
  function onAutoLaunchOff (wasAutoLaunchSet) {
    if (wasAutoLaunchSet) {
      launchableApp.autoLaunch = false;
      console.log('Auto-launch conflict : ' + launchableApp.name + ' was deactivated.');
    }
    else {
      console.error('Auto-launch conflict : Cannot deactivate app ' + launchableApp.name + '.');
    }
  }

  if (launchableApp.isMachineLogicApplication()) {
    return machinelogic.machinemotion.auto_launch_off(launchableApp.name, onAutoLaunchOff);
  }
  else {
    return pythonControl.turnOffAutoLaunch(launchableApp.name, onAutoLaunchOff);
  }
}

/**
 * Plays or resumes an app from the GUI
 * @param {number[]} machineLogicApplicationIndexes
 * @param {AppLauncherGlobalVariables} globalVariables
 */
function playApplication (machineLogicApplicationIndexes, globalVariables) {
  let showSpinnerTimeout;
  function callback_play (success) {
    if (success) {
      globalVariables.appStatus = gStateEnum.running;
      $(PLAYING_APPLICATION_NAME_QUERY_SELECTOR).text(globalVariables.currentPlayingApp.name);
    }
    else {
      console.error('Error while starting application');
    }
    clearTimeout(showSpinnerTimeout);
    _hideButton('spinnerButton');
    _showButton('playButton');
    updateApplicationStatus(machineLogicApplicationIndexes, globalVariables);
  }

  function callback_resume (success) {
    if (success) {
      globalVariables.appStatus = gStateEnum.running;
    }
    else {
      console.error('Error while resuming application');
    }

    updateApplicationStatus(machineLogicApplicationIndexes, globalVariables);
  }

  if (globalVariables.currentPlayingApp.isMachineLogicApplication()) {
    if (globalVariables.currentPlayingApp.machineLogicApplication.hasRobotSimulateInstruction()) {
      if (!machinelogic.isRobotReady(globalVariables.currentPlayingApp.machineLogicApplication)) {
        return;
      }

      if (!globalVariables.currentPlayingApp.machineLogicApplication.canPlayRobotApp()) {
        machinelogic.messageBox('Your robot is not connected. Go to the configuration pane to connect your robot', 'error');

        return;
      }
    }
  }

  // Transition to the RUN state
  if (globalVariables.appStatus === gStateEnum.paused) {
    // Send resume app command
    if (globalVariables.currentPlayingApp.isMachineLogicApplication()) {
      globalVariables.currentPlayingApp.getMachineLogicApplication().resume(callback_resume);
    }
    else {
      pythonControl.resume(globalVariables.currentPlayingApp.name, callback_resume);
    }
  }
  else {
    // Send launch app command
    // only show spinner button if the play request takes too long to complete
    _disable_button('playButton');

    showSpinnerTimeout = setTimeout(() => {
      _hideButton('playButton');
      _showButton('spinnerButton');
    }, 200);

    if (globalVariables.currentPlayingApp.isMachineLogicApplication()) {
      globalVariables.currentPlayingApp.getMachineLogicApplication().play(globalVariables.runtimeConfig, callback_play);
    }
    else {
      pythonControl.play(globalVariables.currentPlayingApp.name, callback_play);
    }
  }
}

/**
 * Function to pause an app from the GUI
 *
 * @param {number[]} machineLogicApplicationIndexes
 * @param {AppLauncherGlobalVariables} globalVariables
 */
function pauseApplication (machineLogicApplicationIndexes, globalVariables) {
  function lPauseCallback (_pData) {
    if (_pData) {
      globalVariables.appStatus = gStateEnum.paused;
    }
    else {
      console.error('Error while pausing application');
    }
    updateApplicationStatus(machineLogicApplicationIndexes, globalVariables);
  }

  // Pause app only if it is running
  if (globalVariables.appStatus !== gStateEnum.running) {
    console.error('Cannot pause while not running');

    return;
  }

  // Send pause app command
  if (globalVariables.currentPlayingApp.isMachineLogicApplication()) {
    globalVariables.currentPlayingApp.getMachineLogicApplication().pause(lPauseCallback);
  }
  else {
    pythonControl.pause(globalVariables.currentPlayingApp.name, lPauseCallback);
  }
}

/**
 * Function to stop an app from the GUI
 *
 * @param {number[]} machineLogicApplicationIndexes
 * @param {AppLauncherGlobalVariables} globalVariables
 */
function stopApplication (machineLogicApplicationIndexes, globalVariables) {
  // TODO : Add a stop motion command ?

  function callback_stop (_pData) {
    // if success
    if (_pData) {
      globalVariables.appStatus = gStateEnum.stopped;
    }
    // if error
    else { console.error('Error while stopping application'); }
    updateApplicationStatus(machineLogicApplicationIndexes, globalVariables);
  }

  if (globalVariables.currentPlayingApp.isMachineLogicApplication()) {
    globalVariables.currentPlayingApp.getMachineLogicApplication().stop(callback_stop);
  }
  else {
    pythonControl.stop(globalVariables.currentPlayingApp.name, callback_stop);
  }
}

/**
 * Function to poll the status of the running app
 * in order to catch the "completed" status
 *
 * @param {number} interval polling interval in milliseconds
 * @param {number[]} machineLogicApplicationIndexes
 * @param {AppLauncherGlobalVariables} globalVariables
 */
function pollExecutionStatus (interval, machineLogicApplicationIndexes, globalVariables) {
  interval = interval || 250;

  function makeOnStatusReceived (resolve, reject) {
    return pJsonResponse => {
      if (!pJsonResponse) {
        console.error('Error while polling application state');

        return;
      }

      if (pJsonResponse.state === gStateEnum.completed || pJsonResponse.state === gStateEnum.inactive || pJsonResponse.state === gStateEnum.paused) {
        // Application has completed/paused
        globalVariables.appStatus = pJsonResponse.state === gStateEnum.paused ? gStateEnum.paused : gStateEnum.completed;
        updateApplicationStatus(machineLogicApplicationIndexes, globalVariables);
        // Application completed/paused, resolve checkCondition
        resolve(pJsonResponse);
      }
      else {
        // Application did not complete,
        // set timeout for next poll, if application is still running
        setTimeout(checkCondition, interval, resolve, reject);
      }
      // Update sequence display
      if (globalVariables.appStatus !== gStateEnum.stopped) {
        onTrace(pJsonResponse, globalVariables);
      }
    };
  }

  const checkCondition = function (resolve, reject) {
    const lOnStatusReceived = makeOnStatusReceived(resolve, reject);
    // Ask the runner about the status of the current app
    if (globalVariables.appStatus === gStateEnum.running || globalVariables.appStatus === gStateEnum.paused) {
      if (globalVariables.currentPlayingApp.isMachineLogicApplication()) {
        globalVariables.currentPlayingApp.getMachineLogicApplication().status(lOnStatusReceived);
      }
      else {
        pythonControl.status(lOnStatusReceived);
      }
    }
    else {
      resolve(globalVariables.appStatus);
    }
  };

  return new Promise(checkCondition);
}

/**
 * Function to call the _poll_completed_status
 */
function _call_poll_completed_status (mmAppsIndexes, globalVariables) {
  pollExecutionStatus(250, mmAppsIndexes, globalVariables).catch(error => console.log('polling errored : ' + error));
}

/**
 * Update the sequences display according to status
 * @param {ExecutionState} pTraceData
 * @param {AppLauncherGlobalVariables} pGlobalVariables
 */
function onTrace (pTraceData, pGlobalVariables) {
  if (pGlobalVariables.currentPlayingApp.isMachineLogicApplication() && pGlobalVariables.currentPlayingApp.getMachineLogicApplication().hasUi()) {
    // Note (mkosarek) Sequences do not need to be updated if we're looking at the UI.
    // The order of 'onTrace' and 'setState' do not matter here
    if (pGlobalVariables.currentPlayingApp.getOnTrace()) {
      pGlobalVariables.currentPlayingApp.getOnTrace()(pTraceData);
    }

    pGlobalVariables.currentPlayingApp.setState(pTraceData.state);

    return;
  }

  const lSequenceListContainer = $(SEQUENCE_LIST_QUERY_SELECTOR);

  Object.keys(pTraceData.sequences).forEach(function (pValue, pIndex) {
    // Ignore #startup sequence containing configuration information
    if (pValue === '#startup') {
      return;
    }

    const lSeqIndex = pGlobalVariables.currentPlayingApp.sequences.findIndex(function (pSeq) { return pSeq.mName === pValue; });
    const lSeqState = pTraceData.sequences[pValue];
    const lSeqWidget = lSequenceListContainer.children().eq(lSeqIndex);
    const lAnimatedBackground = lSeqWidget.find('.ml_animated_background').removeClass('running waiting paused');
    const lInstructionNumber = lSeqWidget.find('.instruction-number');
    const lInstructionName = lSeqWidget.find('.instruction-name');
    const lSequenceStatus = lSeqWidget.find('.sequence-status');

    switch (lSeqState) {
      case gStateEnum.inactive:
      case gStateEnum.not_started:
      case -1: {
        lInstructionNumber.text('Next instruction #1');
        lInstructionName.text('');
        lSequenceStatus.text(gSeqStateEnum.inactive);
        break;
      }
      case gStateEnum.completed: {
        lInstructionNumber.text('Last instruction ' + lInstructionNumber.text().substr(lInstructionNumber.text().indexOf('#')));
        lInstructionName.text('');
        lSequenceStatus.text(gSeqStateEnum.completed);
        break;
      }
      default: {
        if (pGlobalVariables.currentPlayingApp.isPythonScript()) {
          lAnimatedBackground.addClass('running');
          lSequenceStatus.text(gSeqStateEnum.running);
          lInstructionNumber.text('');
          break;
        }

        const lInstructionIndex = pTraceData.sequences[pValue];
        const lCurrentSequence = pGlobalVariables.currentPlayingApp.sequences[pIndex];
        const lCurrentInstruction = lCurrentSequence.mInstructions ? lCurrentSequence.mInstructions[lInstructionIndex] : undefined;

        if (pGlobalVariables.appStatus === gStateEnum.paused) {
          lAnimatedBackground.addClass('paused');
          lInstructionNumber.text('Next instruction #' + (lInstructionIndex + 1));
          lSequenceStatus.text(gSeqStateEnum.paused);
        }
        else {
          lInstructionNumber.text('Current instruction #' + (lInstructionIndex + 1));
          if (lCurrentInstruction) {
            // Check if the instruction is waiting
            if (lCurrentInstruction.mCategory === 'waitFor') {
              lAnimatedBackground.addClass('waiting');
              lSequenceStatus.text('Waiting');
            }
            else {
              lAnimatedBackground.addClass('running');
              lSequenceStatus.text(gSeqStateEnum.running);
            }
          }
          else {
            // No current instruction, the main sequence is waiting on some child or continuous move
            lAnimatedBackground.addClass('waiting');
            lInstructionNumber.text('Last instruction #' + (lInstructionIndex + 1));
            lSequenceStatus.text(gSeqStateEnum.running);
          }
        }

        // Update the instruction name
        if (lCurrentInstruction) {
          // Check if the instruction is waiting
          lCurrentInstruction.mCategory === 'waitFor' ? lInstructionName.text(gSeqStateEnum.waiting + ' (' + lCurrentInstruction.mName + ')') : lInstructionName.text(lCurrentInstruction.mName);
        }
        else {
          // No current instruction, the main sequence is waiting on some child or continuous move
          lInstructionName.text('Child sequence or continuous move');
        }

        break;
      }
    }
  });
}

/**
 * Update the display according to status
 *
 * @param {number[]} machineLogicApplicationIndexes
 * @param {AppLauncherGlobalVariables} globalVariables
 */
function updateApplicationStatus (machineLogicApplicationIndexes, globalVariables) {
  // Change the display of the app status
  function lChangeAppStatus (status) {
    $(PLAYING_APPLICATION_STATUS_QUERY_SELECTOR).text('(' + status + ')');
    const _lSidebarAppContainer = $('#App' + globalVariables.currentPlayingApp.number);
    const _lAnimatedBackground = _lSidebarAppContainer.find('.ml_animated_background');

    // Note (Ajay) UIB does not require state updates as it obtains the state of the application from its trace, However since we are
    // not sending the trace when the application state is "completed" (line 139 -150) we update the state here when the application
    // is stopped or completed, so our UI responds appropriately.
    const lIsUIBApp = globalVariables.currentPlayingApp.isMachineLogicApplication() && globalVariables.currentPlayingApp.getMachineLogicApplication().hasUi();
    !lIsUIBApp && globalVariables.currentPlayingApp.setState(status);

    _lAnimatedBackground.removeClass('running waiting paused');
    switch (status.toLowerCase()) {
      case 'running': {
        _lAnimatedBackground.show().addClass('running').css('z-index', '0');
        break;
      }
      case 'paused': {
        _lAnimatedBackground.show().addClass('paused');
        lIsUIBApp && globalVariables.currentPlayingApp.setState(gStateEnum.paused);
        break;
      }
      case 'waiting': {
        _lAnimatedBackground.show().addClass('waiting');
        break;
      }
      case 'stopped':
      case 'completed': {
        _lAnimatedBackground.hide().css('z-index', '-1');
        $(SEQUENCE_LIST_QUERY_SELECTOR).find('.ml_animated_background').removeClass('running waiting paused');
        lIsUIBApp && globalVariables.currentPlayingApp.setState(gStateEnum.completed);
        break;
      }
    }
  }

  const applicationCards = $(APPLICATION_CARDS_QUERY_SELECTOR);
  switch (globalVariables.appStatus) {
    // App stopped
    case gStateEnum.stopped:
      applicationCards.removeClass('disabled');
      lChangeAppStatus(gSeqStateEnum.stopped);
      $('.sequence-status').text('');
      break;

      // App running
    case gStateEnum.running :
      applicationCards.addClass('disabled');
      lChangeAppStatus(gSeqStateEnum.running);
      // Begin polling to detect completed status
      _call_poll_completed_status(machineLogicApplicationIndexes, globalVariables);
      break;

      // App paused
    case gStateEnum.paused :
      applicationCards.addClass('disabled');
      lChangeAppStatus(gSeqStateEnum.paused);
      break;

      // App completed
    case gStateEnum.completed :
      applicationCards.removeClass('disabled');
      lChangeAppStatus(gSeqStateEnum.completed);
      break;
  }

  _update_buttons(machineLogicApplicationIndexes, globalVariables);
}

/**
 * Update the buttons according to the displayed app
 *
 * @param {number[]} machineLogicApplicationIndexes
 * @param {AppLauncherGlobalVariables} globalVariables
 */
function _update_buttons (machineLogicApplicationIndexes, globalVariables) {
  switch (globalVariables.appStatus) {
    // App running
    case gStateEnum.running :
      if (FeatureFlags.HIDE_PAUSE_BUTTON) {
        _disable_button('playButton');
        _enable_button('stopButton');
      }
      else {
        _hideButton('playButton');
        _showButton('pauseButton');
        globalVariables.currentPlayingApp.isMachineLogicApplication() ? _enable_button('pauseButton') : _disable_button('pauseButton');
        _enable_button('stopButton');
      }
      break;

      // App paused
    case gStateEnum.paused :
      globalVariables.playButtonOnClick = function () { playApplication(machineLogicApplicationIndexes, globalVariables); };
      _enable_button('playButton');
      _showButton('playButton');
      _hideButton('pauseButton');
      _disable_button('pauseButton');
      _enable_button('stopButton');
      break;

      // App completed or stopped
    default :
      globalVariables.playButtonOnClick = function () { playApplication(machineLogicApplicationIndexes, globalVariables); };
      _enable_button('playButton');
      _showButton('playButton');
      _hideButton('pauseButton');
      _disable_button('pauseButton');
      _disable_button('stopButton');
      break;
  }
}

/**
 * Functions to disable and enable buttons in the app launcher, according to the app state
 */
function _disable_button (buttonName) {
  document.getElementById(buttonName).disabled = true;
}
function _enable_button (buttonName) {
  document.getElementById(buttonName).disabled = false;
}

function _hideButton (pButtonName) {
  $('#' + pButtonName).hide();
}

function _showButton (pButtonName) {
  $('#' + pButtonName).show();
  if (pButtonName === 'spinnerButton') {
    $('#' + pButtonName).css('display', 'flex');
  }
}

export default {
  gStateEnum,
  gSeqStateEnum,

  playApplication,
  turnOffApplicationAutoLaunch,
  pauseApplication,
  stopApplication,
  onTrace,
  updateApplicationStatus,
};
